<template>
    <div
        v-if="!$wait.is('loading-page')"
        class="m-products m-products__single"
    >
        <y-container>
            <y-row class="m-products__single__header">
                <y-button
                    icon="arrow-right"
                    tag="router-link"
                    :to="{ name: 'products-list' }"
                    class="mobile-mode"
                    color="white"
                    size="medium"
                    flat
                />
                <y-col md="4">
                    <y-image-gallery
                        v-if="album"
                        type="Video"
                        :album="album || []"
                        image-version="medium"
                    />
                    <y-image
                        v-else
                        :src="imgSrc(product.image, 'main')"
                        :alt="product.title"
                        width="370px"
                        height="300px"
                    />
                </y-col>
                <y-col md="4">
                    <y-ware-detail
                        type="simple"
                        :add-to-cart="true"
                        :favorite-icon="true"
                        button-color="primary"
                        :product="product"
                        :wares="wares"
                        :rate-average="product.rate_average"
                        :rate-count="product.rate_count"
                        @updateCart="addToCart($event)"
                        @alertMe="$refs.alertMe.$children[0].openAlertMeModal()"
                    />
                </y-col>
                <y-col md="4">
                    <y-specs
                        type="simple"
                        :title="$t('products.specs.title')"
                        :post-id="product.id"
                        :bordered="true"
                    />

                    <div class="socials-items">
                        <h6 class="title">
                            {{ $t('products.share') }}
                        </h6>
                        <y-socials
                            class="d-flex"
                            :twitter="$settings('socials').twitter"
                            :whatsapp="$settings('socials').whatsapp"
                            :telegram="$settings('socials').telegram"
                            :facebook="$settings('socials').facebook"
                            color="body"
                            :share="share"
                        />
                    </div>
                </y-col>
            </y-row>

            <y-section-title
                v-if="related.length"
                type="standard"
                :title="$t('products.similar_posts')"
                :link="{ name: 'products-list' }"
                :link-text="$t('products.see_more')"
                :has-icon="true"
            />

            <y-list-wrapper
                type="grid"
                :col="{
                    xs: '12',
                    sm: '6',
                    md: '4',
                    lg: '3',
                }"
                :component="{
                    classes:'mt-5',
                    name : 'YProductCard',
                    type : 'Picture',
                    imageVersion: 'main',
                    addToCart: true,
                    listeners: {
                        'click:addToCart': (item) => {
                            addToCart(item);
                        },
                    },
                }"
                :limit="related.length"
                :items="related"
                slide
            />

            <y-row v-if="product">
                <y-col md="8">
                    <div class="desktop-mode">
                        <div class="m-products__single__information">
                            <div class="section-selector">
                                <div
                                    v-for="item in sections"
                                    :key="item"
                                    v-scroll-to="`#${item}`"
                                    :to="{ name: $route.name, hash: `#${item}` }"
                                    :class="[
                                        'section-selector__route-item',
                                        { 'active' : `#${item}` === '#intro', }
                                    ]"
                                >
                                    {{ $t(`products.sections.${item}`) }}
                                </div>
                            </div>
                        </div>

                        <h5
                            v-if="product.synopsis"
                            class="typo-h5 color-body-text"
                        >
                            {{ $t('products.sections.intro') }}
                        </h5>
                        <y-page-content
                            id="intro"
                            :content="product.synopsis"
                        />

                        <y-specs
                            id="specs"
                            type="simple"
                            :post-id="product.id"
                            class="m-products__single__specs"
                            :title="$t('products.sections.specs')"
                        />
                    </div>

                    <div class="mobile-mode">
                        <y-accordion-item
                            v-if="product.synopsis"
                            id="products-sections-intro"
                            :title="$t('products.sections.intro')"
                            class="accordion-item"
                            active
                        >
                            <y-page-content :content="product.synopsis" />
                        </y-accordion-item>

                        <y-accordion-item
                            v-if="product.id"
                            id="products-sections-specs"
                            :title="$t('products.sections.specs')"
                            class="accordion-item"
                            active
                        >
                            <y-specs
                                id="specs"
                                type="simple"
                                :post-id="product.id"
                                class="m-products__single__specs"
                            />
                        </y-accordion-item>
                    </div>

                    <h5
                        id="rating"
                        class="typo-h5 color-body-text"
                    >
                        {{ $t('products.sections.rating') }}
                    </h5>
                    <y-products-rate
                        type="simple"
                        :rate-count="product.rate_count"
                        :rate-average="product.rate_average"
                        class="mb-10 pb-3"
                        :post-id="product.id"
                        :rate-config="rateConfig"
                        :user-rate="get(product, 'user_rate', 0)"
                        :rate-items="product.rate_items"
                    />

                    <!-- Comments section -->
                    <y-comments-list
                        id="comments"
                        :url-encode="$route.params.url_encode"
                        type="simple"
                        :title="$t('products.comments.list')"
                        :button-text="$t('products.comments.button')"
                        button-link="#r-comments-form"
                        comments-per-page="5"
                        post-type="products"
                        :scroll-button="true"
                        :pagination="true"
                    />
                    <y-comments-form
                        :post-id="product.id"
                        :configs="commentsConfig"
                        type="metro"
                        :title="$t('products.comments.form')"
                        color="primary"
                    />
                </y-col>
            </y-row>
        </y-container>

        <y-alert-me
            ref="alertMe"
            type="simple"
            :post-id="product.id"
            :channels="product.availability_alert_channels"
        />
    </div>
</template>

<script>

    import {
        YSpecs,
        YListWrapper,
        YImageGallery,
        YSectionTitle,
        YCommentsList,
        YCommentsForm,
        YWareDetail,
        YAlertMe,
        YProductsRate,
    } from '@rogues';
    import {
        YAccordionItem, YSocials, YPageContent, YImage,
    } from '@components/index';

    import PageMixin from '@nodes/mixins/Page';
    import CartMixin from '@nodes/mixins/Cart';

    export default {
        name: 'ProductsSingle',

        components: {
            YSpecs,
            YSocials,
            YWareDetail,
            YPageContent,
            YListWrapper,
            YSectionTitle,
            YCommentsList,
            YCommentsForm,
            YImageGallery,
            YAccordionItem,
            YAlertMe,
            YProductsRate,
            YImage,
        },

        mixins: [PageMixin, CartMixin],

        /**
         * @inheritDoc
         */
        data() {
            return {
                product       : null,
                wares         : null,
                album         : null,
                commentsConfig: null,
                related       : null,
                rateConfig    : null,

                sections: [
                    'intro',
                    'specs',
                    'rating',
                    'comments',
                ],
            };
        },

        /**
         * @inheritDoc
         */
        metaInfo() {
            return {
                title: this.title,
            };
        },

        computed: {
            /**
             * Page title
             * 
             * @returns {string}
             */
            title() {
                if (this.product) {
                    return this.$t('products.titles.single', [this.product.title]);
                }
                return this.$t('products.titles.list');
            },

            /**
             * Return Share data
             *
             * @returns {object}
             */
            share() {
                return {
                    url  : window.location.href,
                    title: this.get(this.product, 'title'),
                };
            },
        },

        methods: {
            /**
             * Return best version of image
             *
             * @param {object|Array} img
             * @param {string} imageVersion
             * @returns {string}
             */
            imgSrc(img, imageVersion) {
                if (Array.isArray(img)) {
                    return this.get(img, `0.versions.${imageVersion}`, img[0].link);
                }
                return this.get(img, `versions.${imageVersion}`, img.link);
            },

            /**
             * Fetch single product data
             *
             * @returns {Promise}
             */
            fetch() {
                this.product = null;
                this.wares = null;
                this.album = null;
                this.commentsConfig = null;
                this.related = null;
                this.rateConfig = null;

                const product = this.$services.Content.postSingle({
                    type     : 'products',
                    slug     : this.$route.params.url_encode,
                    locale   : this.$i18n.locale,
                    including: ['title', 'image', 'synopsis', 'ware_is_available', 'custom',
                                'albums', 'product_tagging_categories', 'similar_markets_by_tagging',
                                'url_encode', 'availability_alert_channels', 'rate_average', 'user_favoring',
                                'rate_count', 'user_rate', 'rate_items', 'available_wares_count'],
                }).catch((error) => {
                    this.handleError(error);
                });

                const productWares = this.$services.Market.productWares({
                    including: ['code', 'original_price', 'sale_price', 'discount',
                                'currency_title', 'is_available', 'title', 'labels'],
                    type  : 'products',
                    slug  : this.$route.params.url_encode,
                    locale: this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                const commentsConfig = this.$services.Commenting.getFormConfig({
                    slug: this.$route.params.url_encode,
                    type: 'products',
                }).catch((error) => {
                    this.handleError(error);
                });

                return Promise.all([product, productWares, commentsConfig]).then((responses) => {
                    if (this.handleReq(responses, 0)) {
                        this.rateConfig = {
                            usersOnly    : this.get(responses[0].data.metadata, 'configs.rate_users_only'),
                            publicResults: this.get(responses[0].data.metadata, 'configs.rate_public_results'),
                        };

                        this.product = responses[0].data.results;
                        this.related = responses[0].data.results.similar_markets_by_tagging;

                        // Create Slideshow
                        if (this.product.albums) {
                            this.album = this.product.albums.filter((item) => item.group === 'album');
                            const videos = this.product.albums.filter((item) => item.group === 'videos');
                            if (this.get(this.product, 'image')) {
                                this.album.unshift(this.get(this.product, 'image'));
                            } if (videos.length) {
                                this.album.unshift(...videos);
                            }
                        }
                    }
                    if (this.handleReq(responses, 1)) {
                        this.wares = responses[1].data.results;
                    }

                    if (this.handleReq(responses, 2)) {
                        this.commentsConfig = {
                            ...responses[2].data.results,
                            recaptcha: responses[2].data.metadata.recaptcha,
                        };
                    }
                }).catch((error) => {
                    this.handleError(error);
                });
            },
        },
    };
</script>
